/**
 * versona_metrics_mind.ts — the MIND of the world (2026-09-20).
 *
 * The largest tables in Atlas are cognition: `versona_memory` 121k,
 * `versona_belief` 116k, `memory_provenance` 115k, `price_belief` 35k,
 * `versona_intent` 20k, `versona_thought` 19k, `versona_opinion` 16k,
 * `versona_emotion` 15k, `versona_aspiration` 11k, `npc_mood` / `npc_mind` 21k
 * — roughly 490,000 rows. Every one of them was read `WHERE versona_id = …`,
 * one versona at a time in the dossier. Nothing had ever aggregated them, so
 * the window named "Versona Universe" could show you a mind but never the mind
 * of the world.
 *
 * Pure, like its siblings: rows in, cards out. The module fetches and projects.
 *
 * TWO MEASUREMENT RULES worth stating, because both change what the numbers
 * mean:
 *   · MOOD IS MEASURED AGAINST ITS OWN BASELINE. `npc_mood` carries each
 *     versona's base PAD beside their current PAD. A world at pleasure −21 is
 *     not miserable if that is where its people sit by temperament; what says
 *     something is the DISPLACEMENT from each one's own baseline.
 *   · EMOTIONS ARE A STOCK, NOT A RATE. `versona_emotion` rows decay on their
 *     own half-life, so "fear 10,310 against joy 88" partly reflects that fear
 *     lingers. The card reports the mean half-life beside the tally so the
 *     reader can tell a frightened world from a slow-fading one.
 */
import { type Metric, type MetricGroup, stat, tally, drill, int, f1, pct } from './versona_metrics.js';

export interface MBelief { kind: string; weight: number; confidence: number }
export interface MProv { hops: number; confidence: number }
export interface MMood { p: number; a: number; d: number; bp: number; ba: number; bd: number }
export interface MMind { joy: number; anger: number; fear: number; hungerStreak: number }
export interface MEmotion { etype: string; intensity: number; halfLifeS: number }
export interface MAspiration { kind: string; progress: number }
export interface MOpinion { lean: number }
export interface MIntent { activity: string; commitment: number }
export interface MFaith { faithId: string; intensity: number }
export interface MPriceBelief { commodity: string; mu: number; sigma: number }
export interface MTruth { commodity: string; avgPrice: number; epoch: number }
export interface MKin { kind: string }
export interface MPlayerRel { versonaId: string; nemesis: boolean; rival: boolean; seenCount: number; talkStreak: number }

export interface MindMetricsInput {
  beliefs: MBelief[]; prov: MProv[]; moods: MMood[]; minds: MMind[]; emotions: MEmotion[];
  aspirations: MAspiration[]; opinions: MOpinion[]; intents: MIntent[]; faiths: MFaith[];
  priceBeliefs: MPriceBelief[]; truth: MTruth[]; kin: MKin[]; playerRels: MPlayerRel[];
  /** How many versonas there are, so "X of the world" lines have a denominator. */
  population: number;
}

export function emptyMindInput(): MindMetricsInput {
  return {
    beliefs: [], prov: [], moods: [], minds: [], emotions: [], aspirations: [], opinions: [],
    intents: [], faiths: [], priceBeliefs: [], truth: [], kin: [], playerRels: [], population: 0,
  };
}

const share = (n: number, d: number): number => (d > 0 ? n / d : 0);
const by = <T>(rows: readonly T[], f: (r: T) => string, top = 12): Array<{ label: string; value: number; share: number }> =>
  tally(rows.map(f).filter((k) => k !== '')).slice(0, top);
const ofTotal = (label: string, n: number, d: number, hint?: string): Metric =>
  ({ label, value: d > 0 ? `${int(n)} of ${int(d)} · ${pct(share(n, d))}` : '—', hint });
/** A signed number reads better with its sign when the sign is the point. */
const signed = (x: number): string => `${x > 0 ? '+' : ''}${f1(x)}`;

export function computeMindMetrics(m: MindMetricsInput): MetricGroup[] {
  const out: MetricGroup[] = [];
  const push = (g: MetricGroup, any: number): void => { if (any > 0) out.push(g); };

  // ── The mood of the world, against its own baseline ────────────────────────
  {
    const disp = (cur: (x: MMood) => number, base: (x: MMood) => number): ReturnType<typeof stat> =>
      stat(m.moods.map((x) => (cur(x) - base(x)) / 10));
    const dp = disp((x) => x.p, (x) => x.bp), da = disp((x) => x.a, (x) => x.ba), dd = disp((x) => x.d, (x) => x.bd);
    const below = m.moods.filter((x) => x.p < x.bp).length;
    const hungry = m.minds.filter((x) => x.hungerStreak > 0).length;
    push({
      id: 'mind-mood', cat: 'mind', title: 'The mood of the world',
      metrics: [
        ofTotal('minds measured', m.moods.length, m.population),
        { label: 'pleasure · now / vs own baseline', value: m.moods.length ? `${f1(stat(m.moods.map((x) => x.p / 10)).mean)} / ${signed(dp.mean)}` : '—', hint: 'PAD is per-versona: each one carries their own base temperament, so the DISPLACEMENT is the signal, not the level' },
        { label: 'arousal · now / vs baseline', value: m.moods.length ? `${f1(stat(m.moods.map((x) => x.a / 10)).mean)} / ${signed(da.mean)}` : '—' },
        { label: 'dominance · now / vs baseline', value: m.moods.length ? `${f1(stat(m.moods.map((x) => x.d / 10)).mean)} / ${signed(dd.mean)}` : '—' },
        ofTotal('below their own baseline', below, m.moods.length, 'unhappier than their temperament — the share of the world having a bad day'),
        { label: 'worst displacement', value: m.moods.length ? f1(dp.min) : '—', hint: 'the single most miserable versona relative to themselves' },
        ofTotal('carrying a hunger streak', hungry, m.minds.length, 'npc_mind.hunger_streak — shortage biting, one epoch at a time'),
        { label: 'joy / anger / fear (agent affect)', value: m.minds.length ? `${f1(stat(m.minds.map((x) => x.joy)).mean)} / ${f1(stat(m.minds.map((x) => x.anger)).mean)} / ${f1(stat(m.minds.map((x) => x.fear)).mean)}` : '—' },
      ],
      bars: [
        { label: 'pleasure (displacement)', value: Math.round(dp.mean * 10) / 10, share: Math.min(1, Math.abs(dp.mean) / 30) },
        { label: 'arousal (displacement)', value: Math.round(da.mean * 10) / 10, share: Math.min(1, Math.abs(da.mean) / 30) },
        { label: 'dominance (displacement)', value: Math.round(dd.mean * 10) / 10, share: Math.min(1, Math.abs(dd.mean) / 30) },
      ],
    }, m.moods.length + m.minds.length);
  }

  // ── The emotional weather ──────────────────────────────────────────────────
  {
    const t = by(m.emotions, (e) => e.etype, 12);
    const fear = m.emotions.filter((e) => e.etype === 'fear').length;
    const joy = m.emotions.filter((e) => e.etype === 'joy').length;
    const hl = stat(m.emotions.map((e) => e.halfLifeS).filter((x) => x > 0));
    push({
      id: 'mind-emotion', cat: 'mind', title: 'The emotional weather',
      metrics: [
        { label: 'live emotions', value: int(m.emotions.length), hint: 'a row is one FELT thing, decaying on its own half-life' },
        { label: 'distinct feelings', value: int(t.length) },
        { label: 'intensity · mean / max', value: m.emotions.length ? `${f1(stat(m.emotions.map((e) => e.intensity)).mean)} / ${f1(stat(m.emotions.map((e) => e.intensity)).max)}` : '—' },
        { label: 'fear : joy', value: joy > 0 ? `${f1(fear / joy)} : 1` : fear > 0 ? `${int(fear)} : 0` : '—', hint: 'a STOCK, not a rate — read it beside the half-life below before calling the world frightened' },
        { label: 'mean half-life', value: hl.n ? `${f1(hl.mean / 60)} min` : '—', hint: 'how long a feeling lingers; a long one inflates its own tally' },
        { label: 'feelings per versona', value: m.population ? f1(m.emotions.length / m.population) : '—' },
      ],
      bars: t,
    }, m.emotions.length);
  }

  // ── What they believe ──────────────────────────────────────────────────────
  {
    const conf = stat(m.beliefs.map((b) => b.confidence));
    const w = stat(m.beliefs.map((b) => b.weight));
    const against = m.beliefs.filter((b) => b.weight < 0).length;
    const forIt = m.beliefs.filter((b) => b.weight > 0).length;
    const aboutPlayer = m.beliefs.filter((b) => b.kind === 'player').length;
    push({
      id: 'mind-belief', cat: 'mind', title: 'What the world believes',
      metrics: [
        { label: 'beliefs held', value: int(m.beliefs.length) },
        { label: 'per versona', value: m.population ? f1(m.beliefs.length / m.population) : '—' },
        { label: 'confidence · mean / max', value: m.beliefs.length ? `${f1(conf.mean)} / ${f1(conf.max)}` : '—', hint: 'how sure they are; hearsay arrives discounted' },
        { label: 'for / against / neutral', value: `${int(forIt)} / ${int(against)} / ${int(m.beliefs.length - forIt - against)}`, hint: 'the sign of the weight — a belief can be held FOR or AGAINST its subject' },
        { label: 'weight · mean', value: m.beliefs.length ? signed(w.mean) : '—' },
        { label: 'beliefs about the PLAYER', value: int(aboutPlayer), hint: 'kind = player: what the world has concluded about you' },
      ],
      bars: by(m.beliefs, (b) => b.kind),
    }, m.beliefs.length);
  }

  // ── How far a rumour travels ───────────────────────────────────────────────
  {
    const maxHop = m.prov.reduce((a, p) => Math.max(a, p.hops), 0);
    const firstHand = m.prov.filter((p) => p.hops === 0).length;
    const rows: Metric[] = [
      { label: 'provenance rows', value: int(m.prov.length), hint: 'one per memory that knows where it came from' },
      ofTotal('first-hand (saw it themselves)', firstHand, m.prov.length),
      ofTotal('hearsay (heard it from someone)', m.prov.length - firstHand, m.prov.length),
      { label: 'deepest chain', value: maxHop > 0 ? `${int(maxHop)} hop${maxHop === 1 ? '' : 's'}` : '—', hint: 'how far the furthest story has been passed along' },
    ];
    // The decay curve, hop by hop — the shape of the gossip network.
    for (let h = 0; h <= Math.min(maxHop, 5); h++) {
      const at = m.prov.filter((p) => p.hops === h);
      if (at.length === 0) continue;
      rows.push({ label: `confidence at ${h} hop${h === 1 ? '' : 's'}`, value: `${f1(stat(at.map((p) => p.confidence)).mean)} · ${int(at.length)} stories` });
    }
    const hopBars: Array<{ label: string; value: number; share: number }> = [];
    for (let h = 0; h <= Math.min(maxHop, 6); h++) {
      const n = m.prov.filter((p) => p.hops === h).length;
      if (n > 0) hopBars.push({ label: `${h} hop${h === 1 ? '' : 's'}`, value: n, share: share(n, m.prov.length) });
    }
    push({ id: 'mind-rumour', cat: 'mind', title: 'How far a rumour travels', metrics: rows, bars: hopBars }, m.prov.length);
  }

  // ── What they want ─────────────────────────────────────────────────────────
  {
    const prog = stat(m.aspirations.map((a) => a.progress));
    const stalled = m.aspirations.filter((a) => a.progress === 0).length;
    const nearly = m.aspirations.filter((a) => a.progress >= 75).length;
    push({
      id: 'mind-aspiration', cat: 'mind', title: 'What they want, and how far along',
      metrics: [
        ofTotal('holding an aspiration', m.aspirations.length, m.population),
        { label: 'progress · mean / median', value: m.aspirations.length ? `${f1(prog.mean)} / ${f1(prog.median)}` : '—' },
        ofTotal('no progress at all', stalled, m.aspirations.length, 'they want it and have not moved — a median of 0 means the world is stuck wanting'),
        ofTotal('three quarters there', nearly, m.aspirations.length),
        { label: 'distinct ambitions', value: int(new Set(m.aspirations.map((a) => a.kind)).size) },
      ],
      bars: by(m.aspirations, (a) => a.kind),
    }, m.aspirations.length);
  }

  // ── What they intend ───────────────────────────────────────────────────────
  {
    const c = stat(m.intents.map((i) => i.commitment));
    push({
      id: 'mind-intent', cat: 'mind', title: 'What they intend to do next',
      metrics: [
        { label: 'live intents', value: int(m.intents.length), hint: 'the activity a versona has adopted, until it expires' },
        { label: 'commitment · mean / max', value: m.intents.length ? `${f1(c.mean)} / ${f1(c.max)}` : '—', hint: 'how firmly held — a weak intent yields to the schedule' },
        { label: 'distinct activities', value: int(new Set(m.intents.map((i) => i.activity)).size) },
      ],
      bars: by(m.intents, (i) => i.activity),
    }, m.intents.length);
  }

  // ── The polity's lean ──────────────────────────────────────────────────────
  {
    const lean = m.opinions.map((o) => o.lean / 10);
    const s = stat(lean);
    const edges = [-100, -50, -20, -5, 5, 20, 50, 101];
    const bars = edges.slice(0, -1).map((lo, i) => {
      const hi = edges[i + 1];
      const n = lean.filter((v) => v >= lo && v < hi).length;
      return { label: `${lo} … ${hi === 101 ? 100 : hi}`, value: n, share: share(n, lean.length) };
    }).filter((b) => b.value > 0);
    const right = lean.filter((v) => v > 20).length, left = lean.filter((v) => v < -20).length;
    const middle = lean.length - right - left;
    push({
      id: 'mind-opinion', cat: 'mind', title: 'The polity’s lean',
      metrics: [
        ofTotal('holding an opinion', m.opinions.length, m.population),
        { label: 'lean · mean / median', value: lean.length ? `${signed(s.mean)} / ${signed(s.median)}` : '—', hint: '−100 … +100; the settlement votes are built on this' },
        { label: 'range', value: lean.length ? `${signed(s.min)} … ${signed(s.max)}` : '—' },
        { label: 'right / middle / left', value: `${int(right)} / ${int(middle)} / ${int(left)}`, hint: 'beyond ±20 counts as a side' },
        ofTotal('on one side (beyond ±20)', right + left, lean.length, 'a polity with no middle is a polity that cannot compromise'),
        { label: 'lopsidedness', value: lean.length ? pct(Math.abs(right - left) / Math.max(1, right + left)) : '—', hint: 'how far the two sides are from even — 0 % is a balanced house' },
      ],
      bars,
    }, m.opinions.length);
  }

  // ── Faith ──────────────────────────────────────────────────────────────────
  {
    const inten = stat(m.faiths.map((f) => f.intensity));
    push({
      id: 'mind-faith', cat: 'mind', title: 'Faith',
      metrics: [
        ofTotal('adhering to a faith', m.faiths.length, m.population),
        { label: 'faiths kept', value: int(new Set(m.faiths.map((f) => f.faithId)).size) },
        { label: 'intensity · mean / max', value: m.faiths.length ? `${f1(inten.mean)} / ${f1(inten.max)}` : '—' },
        { label: 'devout (intensity ≥ 70)', value: int(m.faiths.filter((f) => f.intensity >= 70).length) },
      ],
      bars: by(m.faiths, (f) => f.faithId),
    }, m.faiths.length);
  }

  // ── What they think things cost, against what things cost ──────────────────
  {
    const truthNow = new Map<string, number[]>();
    const lastEpoch = m.truth.reduce((a, t) => Math.max(a, t.epoch), -Infinity);
    for (const t of m.truth) {
      if (t.epoch !== lastEpoch) continue;
      const a = truthNow.get(t.commodity) ?? []; a.push(t.avgPrice); truthNow.set(t.commodity, a);
    }
    const mean = (a: number[]): number => a.reduce((x, y) => x + y, 0) / a.length;
    const held = new Map<string, { mu: number[]; sigma: number[] }>();
    for (const b of m.priceBeliefs) {
      const a = held.get(b.commodity) ?? { mu: [], sigma: [] };
      a.mu.push(b.mu); a.sigma.push(b.sigma); held.set(b.commodity, a);
    }
    const rows: Array<{ k: string; believed: number; actual: number; err: number; n: number }> = [];
    for (const [k, b] of held) {
      const t = truthNow.get(k);
      if (!t || t.length === 0) continue;
      const actual = mean(t);
      if (actual === 0) continue;
      const believed = mean(b.mu);
      rows.push({ k, believed, actual, err: (believed - actual) / actual, n: b.mu.length });
    }
    rows.sort((a, b) => Math.abs(b.err) - Math.abs(a.err));
    const holders = rows.reduce((a, r) => a + r.n, 0);
    const wErr = holders > 0 ? rows.reduce((a, r) => a + Math.abs(r.err) * r.n, 0) / holders : 0;
    const over = rows.find((r) => r.err > 0), under = [...rows].reverse().find((r) => r.err < 0);
    push({
      id: 'mind-prices', cat: 'mind', title: 'What they think things cost',
      metrics: [
        { label: 'price beliefs held', value: int(m.priceBeliefs.length), hint: 'one versona’s estimate of one commodity — this is what they trade on, not the book price' },
        { label: 'commodities they can price', value: int(rows.length) },
        { label: 'mean error, weighted by holders', value: rows.length ? pct(wErr) : '—', hint: 'how wrong the world is about prices right now' },
        { label: 'most OVER-valued', value: over ? `${over.k} · believed ${f1(over.believed)} vs ${f1(over.actual)}` : '—' },
        { label: 'most UNDER-valued', value: under ? `${under.k} · believed ${f1(under.believed)} vs ${f1(under.actual)}` : '—' },
        { label: 'uncertainty (sigma) · mean', value: m.priceBeliefs.length ? f1(stat(m.priceBeliefs.map((b) => b.sigma)).mean) : '—', hint: 'their own confidence interval — a wide sigma haggles harder' },
      ],
      // The bar is the ERROR, so the longest bar is the biggest misconception.
      bars: rows.slice(0, 12).map((r) => ({ label: `${r.k} ${r.err > 0 ? '+' : ''}${Math.round(r.err * 100)} %`, value: Math.round(Math.abs(r.err) * 100), share: Math.min(1, Math.abs(r.err)) })),
    }, m.priceBeliefs.length);
  }

  // ── Kin, and how the world feels about YOU ─────────────────────────────────
  {
    const nem = m.playerRels.filter((r) => r.nemesis);
    const riv = m.playerRels.filter((r) => r.rival);
    const seen = stat(m.playerRels.map((r) => r.seenCount));
    const streak = stat(m.playerRels.map((r) => r.talkStreak));
    push({
      id: 'mind-player', cat: 'mind', title: 'Kin, and how the world feels about you',
      metrics: [
        ofTotal('have met a player', m.playerRels.length, m.population),
        { label: 'sightings · mean / most', value: m.playerRels.length ? `${f1(seen.mean)} / ${int(seen.max)}` : '—' },
        { label: 'talk streak · longest', value: m.playerRels.length ? int(streak.max) : '—', hint: 'consecutive days spoken to — the Stardew habit' },
        { label: 'nemeses', value: int(nem.length), hint: 'a broken net AND a permanent harm: sworn against you, and it does not fade', drill: drill('versona', 'nemeses', nem.map((r) => r.versonaId)) },
        { label: 'rivals', value: int(riv.length), hint: 'the live COLD band — sour, but they still take what you offer, and a real gesture lands double', drill: drill('versona', 'rivals', riv.map((r) => r.versonaId)) },
        { label: 'kinship ties', value: int(m.kin.length), hint: by(m.kin, (k) => k.kind).map((b) => `${b.label} ${b.value}`).join(' · ') },
      ],
      bars: by(m.kin, (k) => k.kind),
    }, m.playerRels.length + m.kin.length);
  }

  return out;
}
